import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, CheckCircle2, Building2, Mail, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TONES = ["Professional", "Assertive", "Creative"];

export default function ApplyFromMatch({ open, onClose, match, loopTitle }) {
  const { refreshUser, openUpgrade, quota } = useAuth();
  const [tone, setTone] = useState("Professional");
  const [pitch, setPitch] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open && match) {
      setPitch(""); setSent(false); setTone("Professional");
      generate("Professional");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, match]);

  const generate = async (toneOverride) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/pitch", {
        company: match.company || "the company",
        job_title: match.title || null,
        job_link: match.applyLink || match.url || null,
        recruiter_name: null,
        tone: toneOverride || tone,
        job_description: match.summary || null,
      });
      setPitch(data.cover_letter);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    if (!match.email || !pitch.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/applications/send", {
        company: match.company || "the company",
        job_title: match.title || null,
        job_link: match.applyLink || match.url || null,
        recruiter_email: match.email,
        recruiter_name: null,
        cover_letter: pitch,
      });
      await refreshUser();
      setSent(true);
      toast.success(data.email_delivered ? "Application sent from your loop match!" : "Application logged (email provider not configured).");
    } catch (e) {
      if (e.response?.status === 402) {
        await refreshUser();
        openUpgrade();
        onClose?.();
        toast.warning("Free trial used up. Upgrade to keep applying.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setSending(false);
    }
  };

  if (!match) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="apply-from-match-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="w-5 h-5 text-orange-400" /> Apply to this match
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            AI writes a tailored pitch from your resume and this matched job, then sends it to the contact email.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mt-2 text-sm space-y-1.5">
          <p className="font-medium">{match.title}</p>
          <p className="text-slate-500 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {match.company} · {match.location}</p>
          <p className="text-amber-400 flex items-center gap-1.5 font-mono"><Mail className="w-3.5 h-3.5" /> {match.email}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-sm text-slate-500 mr-1">Tone:</span>
          {TONES.map((t) => (
            <button key={t} data-testid={`apply-tone-${t.toLowerCase()}`} onClick={() => { setTone(t); generate(t); }}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                tone === t ? "border-orange-500/60 bg-orange-500/15 text-orange-200" : "border-slate-300 text-slate-500 hover:text-slate-800"
              }`}>{t}</button>
          ))}
        </div>

        <Textarea
          data-testid="apply-pitch-textarea"
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          placeholder={generating ? "Generating your tailored pitch…" : "Your AI pitch will appear here."}
          className="mt-4 min-h-[220px] bg-slate-50 border-slate-300 text-slate-800 focus-visible:ring-orange-400/40 leading-relaxed"
        />
        {generating && <p className="text-xs text-orange-300 mt-2 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Writing with AI…</p>}

        <div className="flex items-center justify-between gap-3 mt-5">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            {quota?.is_pro ? "Pro • unlimited" : `${quota?.remaining ?? 0} free application(s) left`}
          </span>
          <Button data-testid="apply-send-button" onClick={send} disabled={sending || generating || !pitch.trim() || sent || !match.email}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors">
            {sent ? <CheckCircle2 className="w-4 h-4 mr-2" /> : sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sent ? "Sent" : sending ? "Sending…" : "Send application"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
